// AdminKnowledge Component - Knowledge Base management
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { 
  BookOpen, FolderPlus, Plus, Edit, Trash2, RefreshCw,
  FileText, HelpCircle, Settings, Users, CreditCard, ShoppingCart, Globe, Shield
} from 'lucide-react';
import { useAdmin } from './AdminContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminKnowledge({ 
  kbSections: propKbSections, 
  kbArticles: propKbArticles,
  adminKey, 
  onRefreshSections, 
  onRefreshArticles 
}) {
  // Always call useAdmin hook
  const contextValues = useAdmin() || {};
  
  // Use props if provided, otherwise fallback to context
  const kbSections = propKbSections || contextValues.kbSections || [];
  const kbArticles = propKbArticles || contextValues.kbArticles || [];
  const api = contextValues.api || axios.create({
    baseURL: `${API_URL}/api`,
    headers: { 'X-Admin-Key': adminKey }
  });
  const fetchKBSections = onRefreshSections || contextValues.fetchKBSections || (() => {});
  const fetchKBArticles = onRefreshArticles || contextValues.fetchKBArticles || (() => {});
  const formatDate = contextValues.formatDate || ((d) => d ? new Date(d).toLocaleDateString('fr-CA') : 'N/A');
  const language = contextValues.language || 'fr';

  const [loading, setLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [sectionDialog, setSectionDialog] = useState(false);
  const [articleDialog, setArticleDialog] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null);

  const getSectionIcon = (iconName) => {
    const icons = {
      FileText: <FileText className="h-4 w-4" />,
      HelpCircle: <HelpCircle className="h-4 w-4" />,
      Settings: <Settings className="h-4 w-4" />,
      Users: <Users className="h-4 w-4" />,
      CreditCard: <CreditCard className="h-4 w-4" />,
      ShoppingCart: <ShoppingCart className="h-4 w-4" />,
      Globe: <Globe className="h-4 w-4" />,
      Shield: <Shield className="h-4 w-4" />,
      BookOpen: <BookOpen className="h-4 w-4" />
    };
    return icons[iconName] || <FileText className="h-4 w-4" />;
  };

  const getSectionArticles = (sectionId) => {
    return kbArticles.filter(a => a.section_id === sectionId);
  };

  const seedKB = async () => {
    setLoading(true);
    try {
      await api.post('/admin/knowledge-base/seed');
      toast.success(language === 'fr' ? 'Base de connaissances initialisée!' : 'Knowledge base initialized!');
      fetchKBSections();
      fetchKBArticles();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setLoading(false);
    }
  };

  const resetKB = async () => {
    if (!window.confirm(language === 'fr' ? 'Réinitialiser toute la base de connaissances?' : 'Reset entire knowledge base?')) return;
    setLoading(true);
    try {
      await api.delete('/admin/knowledge-base/reset');
      toast.success(language === 'fr' ? 'Base de connaissances réinitialisée!' : 'Knowledge base reset!');
      setSelectedSection(null);
      setSelectedArticle(null);
      fetchKBSections();
      fetchKBArticles();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSection = async () => {
    if (!editingSection?.name) return;
    setLoading(true);
    try {
      if (editingSection.section_id) {
        await api.put(`/admin/knowledge-base/sections/${editingSection.section_id}`, editingSection);
      } else {
        await api.post('/admin/knowledge-base/sections', editingSection);
      }
      toast.success(language === 'fr' ? 'Section sauvegardée!' : 'Section saved!');
      setSectionDialog(false);
      setEditingSection(null);
      fetchKBSections();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm(language === 'fr' ? 'Supprimer cette section et tous ses articles?' : 'Delete this section and all its articles?')) return;
    setLoading(true);
    try {
      await api.delete(`/admin/knowledge-base/sections/${sectionId}`);
      toast.success(language === 'fr' ? 'Section supprimée!' : 'Section deleted!');
      if (selectedSection?.section_id === sectionId) {
        setSelectedSection(null);
        setSelectedArticle(null);
      }
      fetchKBSections();
      fetchKBArticles();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveArticle = async () => {
    if (!editingArticle?.title || !editingArticle?.section_id) return;
    setLoading(true);
    try {
      if (editingArticle.article_id) {
        await api.put(`/admin/knowledge-base/articles/${editingArticle.article_id}`, editingArticle);
      } else {
        await api.post('/admin/knowledge-base/articles', editingArticle);
      }
      toast.success(language === 'fr' ? 'Article sauvegardé!' : 'Article saved!');
      setArticleDialog(false);
      setEditingArticle(null);
      fetchKBArticles();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm(language === 'fr' ? 'Supprimer cet article?' : 'Delete this article?')) return;
    setLoading(true);
    try {
      await api.delete(`/admin/knowledge-base/articles/${articleId}`);
      toast.success(language === 'fr' ? 'Article supprimé!' : 'Article deleted!');
      if (selectedArticle?.article_id === articleId) {
        setSelectedArticle(null);
      }
      fetchKBArticles();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="space-y-4">
        {/* KB Header Actions */}
        <div className="flex flex-wrap justify-between gap-4">
          <div className="flex gap-2">
            <Button 
              onClick={() => { 
                setEditingSection({ name: '', description: '', icon: 'FileText', order: kbSections.length + 1 }); 
                setSectionDialog(true); 
              }} 
              variant="outline"
            >
              <FolderPlus className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Nouvelle Section' : 'New Section'}
            </Button>
            <Button 
              onClick={() => { 
                setEditingArticle({ 
                  title: '', 
                  section_id: selectedSection?.section_id || '', 
                  content: '', 
                  tags: [], 
                  language: 'fr', 
                  order: 0 
                }); 
                setArticleDialog(true); 
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Nouvel Article' : 'New Article'}
            </Button>
          </div>
          <div className="flex gap-2">
            {kbSections.length === 0 && (
              <Button onClick={seedKB} variant="outline" disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {language === 'fr' ? 'Initialiser KB' : 'Initialize KB'}
              </Button>
            )}
            {kbSections.length > 0 && (
              <Button onClick={resetKB} variant="outline" className="text-destructive" disabled={loading}>
                <Trash2 className="h-4 w-4 mr-2" />
                {language === 'fr' ? 'Réinitialiser' : 'Reset'}
              </Button>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-4">
          {/* Sections List */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Sections ({kbSections.length})</CardTitle>
            </CardHeader>
            <CardContent className="max-h-[500px] overflow-y-auto">
              <div className="space-y-2">
                {kbSections.map((section) => (
                  <div
                    key={section.section_id}
                    className={`p-3 rounded-lg cursor-pointer transition-colors group ${
                      selectedSection?.section_id === section.section_id 
                        ? 'bg-primary/10 border border-primary' 
                        : 'bg-muted/30 hover:bg-muted/50 border border-transparent'
                    }`}
                    onClick={() => { setSelectedSection(section); setSelectedArticle(null); }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getSectionIcon(section.icon)}
                        <span className="font-medium text-sm">{section.name}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {getSectionArticles(section.section_id).length}
                      </span>
                    </div>
                    <div className="hidden group-hover:flex gap-1 mt-2">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-6 px-2" 
                        onClick={(e) => { e.stopPropagation(); setEditingSection(section); setSectionDialog(true); }}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-6 px-2 text-destructive" 
                        onClick={(e) => { e.stopPropagation(); handleDeleteSection(section.section_id); }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
                {kbSections.length === 0 && (
                  <p className="text-center text-muted-foreground py-8 text-sm">
                    {language === 'fr' ? 'Aucune section. Cliquez "Initialiser KB".' : 'No sections. Click "Initialize KB".'}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Articles List */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">
                Articles {selectedSection ? `(${getSectionArticles(selectedSection.section_id).length})` : ''}
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-[500px] overflow-y-auto">
              <div className="space-y-2">
                {selectedSection ? getSectionArticles(selectedSection.section_id).map((article) => (
                  <div
                    key={article.article_id}
                    className={`p-3 rounded-lg cursor-pointer transition-colors group ${
                      selectedArticle?.article_id === article.article_id 
                        ? 'bg-primary/10 border border-primary' 
                        : 'bg-muted/30 hover:bg-muted/50 border border-transparent'
                    }`}
                    onClick={() => setSelectedArticle(article)}
                  >
                    <p className="font-medium text-sm line-clamp-1">{article.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground uppercase">{article.language}</span>
                    </div>
                    <div className="hidden group-hover:flex gap-1 mt-2">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-6 px-2" 
                        onClick={(e) => { e.stopPropagation(); setEditingArticle(article); setArticleDialog(true); }}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-6 px-2 text-destructive" 
                        onClick={(e) => { e.stopPropagation(); handleDeleteArticle(article.article_id); }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )) : (
                  <p className="text-center text-muted-foreground py-8 text-sm">
                    {language === 'fr' ? 'Sélectionnez une section' : 'Select a section'}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Article Preview */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  {selectedArticle ? selectedArticle.title : (language === 'fr' ? 'Aperçu Article' : 'Article Preview')}
                </CardTitle>
                {selectedArticle && (
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => { setEditingArticle(selectedArticle); setArticleDialog(true); }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-destructive" 
                      onClick={() => handleDeleteArticle(selectedArticle.article_id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="max-h-[450px] overflow-y-auto">
              {selectedArticle ? (
                <div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {selectedArticle.tags?.map((tag, i) => (
                      <span key={i} className="text-xs px-2 py-1 rounded-full bg-muted">{tag}</span>
                    ))}
                  </div>
                  <div className="whitespace-pre-wrap font-mono text-sm bg-muted/30 p-4 rounded-lg">
                    {selectedArticle.content}
                  </div>
                  <p className="text-xs text-muted-foreground mt-4">
                    {language === 'fr' ? 'Mis à jour' : 'Updated'}: {formatDate(selectedArticle.updated_at)}
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  <div className="text-center">
                    <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>{language === 'fr' ? 'Sélectionnez un article' : 'Select an article'}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Section Dialog */}
      <Dialog open={sectionDialog} onOpenChange={setSectionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingSection?.section_id 
                ? (language === 'fr' ? 'Modifier Section' : 'Edit Section')
                : (language === 'fr' ? 'Nouvelle Section' : 'New Section')
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{language === 'fr' ? 'Nom' : 'Name'}</Label>
              <Input 
                value={editingSection?.name || ''} 
                onChange={(e) => setEditingSection({...editingSection, name: e.target.value})}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                value={editingSection?.description || ''} 
                onChange={(e) => setEditingSection({...editingSection, description: e.target.value})}
              />
            </div>
            <div>
              <Label>{language === 'fr' ? 'Icône' : 'Icon'}</Label>
              <select 
                className="w-full p-2 border rounded-md bg-background"
                value={editingSection?.icon || 'FileText'}
                onChange={(e) => setEditingSection({...editingSection, icon: e.target.value})}
              >
                <option value="FileText">FileText</option>
                <option value="HelpCircle">HelpCircle</option>
                <option value="Settings">Settings</option>
                <option value="Users">Users</option>
                <option value="CreditCard">CreditCard</option>
                <option value="ShoppingCart">ShoppingCart</option>
                <option value="Globe">Globe</option>
                <option value="Shield">Shield</option>
                <option value="BookOpen">BookOpen</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSectionDialog(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button onClick={handleSaveSection} disabled={loading}>
              {language === 'fr' ? 'Sauvegarder' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Article Dialog */}
      <Dialog open={articleDialog} onOpenChange={setArticleDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingArticle?.article_id 
                ? (language === 'fr' ? 'Modifier Article' : 'Edit Article')
                : (language === 'fr' ? 'Nouvel Article' : 'New Article')
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{language === 'fr' ? 'Titre' : 'Title'}</Label>
              <Input 
                value={editingArticle?.title || ''} 
                onChange={(e) => setEditingArticle({...editingArticle, title: e.target.value})}
              />
            </div>
            <div>
              <Label>Section</Label>
              <select 
                className="w-full p-2 border rounded-md bg-background"
                value={editingArticle?.section_id || ''}
                onChange={(e) => setEditingArticle({...editingArticle, section_id: e.target.value})}
              >
                <option value="">{language === 'fr' ? 'Sélectionner une section' : 'Select a section'}</option>
                {kbSections.map((s) => (
                  <option key={s.section_id} value={s.section_id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>{language === 'fr' ? 'Contenu' : 'Content'}</Label>
              <Textarea 
                rows={8}
                value={editingArticle?.content || ''} 
                onChange={(e) => setEditingArticle({...editingArticle, content: e.target.value})}
              />
            </div>
            <div>
              <Label>{language === 'fr' ? 'Langue' : 'Language'}</Label>
              <select 
                className="w-full p-2 border rounded-md bg-background"
                value={editingArticle?.language || 'fr'}
                onChange={(e) => setEditingArticle({...editingArticle, language: e.target.value})}
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
              </select>
            </div>
            <div>
              <Label>Tags ({language === 'fr' ? 'séparés par virgule' : 'comma separated'})</Label>
              <Input 
                value={editingArticle?.tags?.join(', ') || ''} 
                onChange={(e) => setEditingArticle({
                  ...editingArticle, 
                  tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArticleDialog(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button onClick={handleSaveArticle} disabled={loading}>
              {language === 'fr' ? 'Sauvegarder' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
